import { InfraredFlight, MergedFlight } from "@nova/protobuf"
import { generateGenericCases } from "../../generateCases"
import { Builder } from "../../sockets"
import {
    CreateMergedFlightArgs,
    getDefaultMergedFlightArgs,
    UpdateMergedFlightArgs,
} from "@nova/serializer/src/client/validators/cubes/mergedFlights"
import { UpdateInfraredFlightArgs } from "@nova/serializer/src/client/validators/flights/infrared"
import {
    addEntitySocket,
    batchUpdateGroupSocket,
    deleteEntitySocket,
    updateEntitySocket,
} from "features/entity-caching/project/slice"
import { v4 as randomUUID } from "uuid"
import { ID } from "@nova/constants"
import { updateInfraredFlight } from "../flights/infrared"
import { selectMergedFlights } from "features/entity-caching/mergedFlights/selectors"
import { createAction as createReduxAction } from "@reduxjs/toolkit"
import { getMergedFlightProperties } from "features/map/inspector-panel/hotspots/mergedFlight.ts/module"
import { clearSelection, selectSingleLayer } from "panels/content/side-panel/slice.new"
import { batchUpdateMergedFlight } from "features/pubsub/entityActions/cubes/mergedFlights"

const {
    create: createMergedFlight,
    update: updateMergedFlight,
    delete: deleteMergedFlight,
    createAction,
    updateAction,
    deleteAction,
} = generateGenericCases<
    MergedFlight, // entity type
    CreateMergedFlightArgs, // create args to send to server
    ["createdBy"], // create args required by action
    UpdateMergedFlightArgs, // update args
    { index?: string; parentId?: string; id?: string; flightIds?: ID[] }, // required input context
    void // server context
>("MergedFlight")

const batchUpdateAction = createReduxAction<{
    id: string
    updates: Partial<Pick<InfraredFlight, UpdateInfraredFlightArgs[number]>>
}>(`batchUpdateMergedFlight`)

export const addMergedFlightsToBuilder = (builder: Builder) =>
    builder
        .addCase(createAction, (store, { payload: { data, context } }, socket) => {
            const state = store.getState()
            const flights = selectMergedFlights(state)
            const name = `Merged Hotspots ${Object.values(flights).length + 1}`
            const optimisticData: MergedFlight = {
                ...getDefaultMergedFlightArgs(state.project.data, context?.parentId),
                ...data,
                name,
                id: context?.id || randomUUID(),
            }

            createMergedFlight(
                socket,
                {
                    ...data,
                    name,
                    id: optimisticData.id,
                    index: optimisticData.index,
                    parentId: optimisticData.parentId,
                },
                undefined
            )
            store.dispatch(
                addEntitySocket({
                    data: optimisticData,
                    path: ["mergedFlights"],
                })
            )

            const mergedProperties = getMergedFlightProperties(context?.flightIds || [])

            context?.flightIds?.map(async (id) => {
                store.dispatch(
                    updateInfraredFlight({
                        id,
                        updates: {
                            mergeId: optimisticData.id,
                            parentId: "null",
                            index: undefined,
                            visible: true,
                            ...mergedProperties,
                        },
                    })
                )
            })
            store.dispatch(selectSingleLayer({ id: optimisticData.id }))
        })
        .addCase(updateAction, (store, { payload: { id, updates } }, socket) => {
            updateMergedFlight(socket, id, updates)
            store.dispatch(updateEntitySocket({ id, updates, path: ["mergedFlights", { id }] }))
            const childUpdates: { visible?: boolean; locked?: boolean } = {}
            if (updates.visible !== undefined) {
                childUpdates.visible = updates.visible
            }
            if (updates.locked !== undefined) {
                childUpdates.locked = updates.locked
            }
            store.dispatch(batchUpdateMergedFlight({ id, updates: childUpdates }))
        })
        .addCase(deleteAction, (store, { payload: { id } }, socket) => {
            deleteMergedFlight(socket, id)
            store.dispatch(deleteEntitySocket({ path: ["mergedFlights", { id }] }))
            store.dispatch(clearSelection())
        })
        .addCase(batchUpdateAction, (store, { payload: { id, updates } }, socket) => {
            socket.emit("batchUpdateMergedFlight", id, updates)
            store.dispatch(
                batchUpdateGroupSocket({
                    path: ["mergedFlights", { id }],
                    updates,
                    groupKey: "mergeId",
                })
            )
        })

export {
    createAction as createMergedFlight,
    updateAction as updatedMergedFlight,
    deleteAction as deleteMergedFlight,
    batchUpdateAction as batchUpdateMergedFlight,
}
